import { tokens } from '@env0/ui-theme';
import { getAntdTheme as getGeneratedAntdTheme } from '@env0/ui-theme/antd';
import type { ThemeConfig } from 'antd';
import merge from 'lodash/merge';
import type { Breakpoints, Env0Theme } from './themes.constants';
import { getLegacyAntdTheme } from './antd.constants.legacy';

/**
 * Every other antd mapping is generated from Figma's `Bridge - AntD` collection. This one is
 * hand-written, and stays that way: which key each component reads is a function of the antd version,
 * not a design decision, so completing the collection in Figma would generate the wrong thing.
 *
 * New-design only: the legacy branch returns a different config entirely, so switch-off is untouched.
 */
const zIndexTokens = {
  zIndexBase: tokens.zIndex.base,
  zIndexPopupBase: tokens.zIndex.dropdown
};

/**
 * Modal has no `zIndexPopup` in its ComponentToken and reads the `zIndexPopupBase` seed directly. The
 * rest read `zIndexPopup`, the value their style consumes; seeding those instead renders the DS number
 * plus antd's per-component offset (popover +30, notification +50, tooltip +70), so no DS value would
 * reach the DOM.
 *
 * The ladder ranks dropdown below drawer and modal, which holds only because AntdWrapper portals a
 * nested popup into its container: 5.6.4 has no `zIndexContext`, so otherwise every popup lands on
 * document.body and a Select inside a Popover renders behind it.
 */
const zIndexComponents = {
  Modal: { zIndexPopupBase: tokens.zIndex.modal },
  Drawer: { zIndexPopup: tokens.zIndex.drawer },
  Popover: { zIndexPopup: tokens.zIndex.popover },
  Notification: { zIndexPopup: tokens.zIndex.toast },
  Tooltip: { zIndexPopup: tokens.zIndex.tooltip }
};

const toPx = (value: string) => parseInt(value, 10);

/**
 * Neither Result nor Skeleton is in the generated bridge and Figma specs neither — extrapolated from
 * the ramp per D1. Takes the mode because the Skeleton pair is colour: fixed light greys would give a
 * near-white shimmer on dark surfaces, so dark sweeps from the dark end of the same scale (700 → 650).
 */
const feedbackComponents = (mode: 'light' | 'dark') => ({
  Result: {
    // h3, not h4: a Result is a full-page outcome.
    titleFontSize: toPx(tokens.typography.fontSize.heading.h3),
    subtitleFontSize: toPx(tokens.typography.fontSize.body.md),
    iconFontSize: toPx(tokens.spacing.scale['1000'])
  },
  Skeleton: {
    gradientFromColor: mode === 'dark' ? tokens.colors.neutral['700'] : tokens.colors.neutral['300'],
    gradientToColor: mode === 'dark' ? tokens.colors.neutral['650'] : tokens.colors.neutral['200'],
    blockRadius: toPx(tokens.spacing.radius['100'])
  }
});

/**
 * Hand-written for the same reason as the z-index mappings: which alias token antd reads is a fact
 * about the version, not a design decision. Button's ComponentToken is empty in 5.6.4, so only the
 * alias tokens scoped here take effect - each was measured reaching the emitted CSS. Real values
 * rather than `var()`: antd derives palettes from what it is given.
 *
 * Mechanics worth knowing before editing:
 * - Radius and label size are read per size variant, so setting only the base keys leaves large and
 *   small on antd's own ladder.
 * - Horizontal padding is `buttonPaddingHorizontal - lineWidth`; the border adds the pixel back, so
 *   the token value is the visual inset. Small's is hardcoded to 8 in antd and is not configurable.
 * - Secondary is antd's `default` type, and Figma binds it no surface variable at all - a border and
 *   a label over nothing, hence `transparent`.
 */
const buttonComponent = (mode: 'light' | 'dark') => {
  const { surface, text, border } = tokens.theme[mode];
  const px = (value: string) => parseInt(value, 10);

  return {
    controlHeight: 32,
    controlHeightLG: 40,
    controlHeightSM: 28,
    borderRadius: px(tokens.spacing.radius['100']),
    borderRadiusLG: px(tokens.spacing.radius['100']),
    borderRadiusSM: px(tokens.spacing.radius['100']),
    lineWidth: px(tokens.spacing.borderWidth.xs),
    fontSize: 14,
    fontSizeLG: 14,
    paddingContentHorizontal: px(tokens.spacing.padding.lg),

    colorPrimary: surface.primary.action,
    colorPrimaryHover: surface.primary['action-hover'],
    colorPrimaryActive: surface.primary['action-hover'],
    colorTextLightSolid: text.onAction,

    colorBgContainer: 'transparent',
    colorBorder: border.secondary,
    // `text.heading`, not the `text.onAction` Figma binds: that token means "over a lime fill" and is
    // black in both modes, so on an unfilled Secondary it vanished in dark. Same value in light.
    colorText: text.heading,

    colorBgContainerDisabled: surface.disabled.action,
    // Dark cannot follow Figma: text.onDisabled and surface.disabled.action are both #4d4d4d there,
    // so the label would be invisible against its own fill. Figma has no dark frames (D3).
    colorTextDisabled: mode === 'dark' ? text.disabled : text.onDisabled
  };
};

/**
 * One token, and only because dark needs it.
 *
 * antd 5.6.4 defaults `horizontalItemSelectedColor` to `colorPrimary` and both horizontal backgrounds
 * to `transparent` (`antd/lib/menu/style/index.js`), so in light the generated bridge's lime already
 * produces Figma's underline-on-nothing and a block here would restate antd's own derivation. In dark
 * it does not: antd's `darkAlgorithm` shifts `colorPrimary` to `#b6d539`, and the DS holds
 * `surface.primary.action` at the same lime in both modes. Setting it pins the token value.
 *
 * Measured in the browser, not reasoned about — the light-only spec passed with this deleted, which is
 * how the first version of this shipped, and it took a dark browser pass to catch.
 *
 * What antd cannot do is give the label a different colour from the underline: `theme.js` draws both
 * from this one token. `components/common/menu.tsx` repaints the label on `.ant-menu-title-content`,
 * which antd gives only a transition, so it needs no `!important` and no doubled class.
 *
 * `Menu.itemSelectedBg` in the generated bridge is the lime — a nav-rail value. It reaches vertical
 * menus only, so it never touches the tab bar; `drift-analysis-menu.tsx` already neutralises it
 * locally, and the rail itself belongs to C2/C3. Raise it there rather than repainting them from here.
 */
const menuComponent = (mode: 'light' | 'dark') => ({
  horizontalItemSelectedColor: tokens.theme[mode].surface.primary.action
});

/**
 * Tabs has exactly one consumer (`components/common/tabs.tsx`), so unlike Menu there is no other
 * family to repaint. Only the keys that actually differ from antd 5.6.4's defaults are set:
 * `inkBarColor` already derives from `colorPrimary` and would be the same lime either way, so setting
 * it would be untestable noise for the same reason Menu has no block.
 *
 * The label tokens do differ — antd defaults them to `colorPrimary` too, which would put a lime label
 * on a lime ink bar. `cardBg` in the generated bridge is `#151514` in both modes, a dark value on a
 * light card; corrected here per mode.
 */
const tabsComponent = (mode: 'light' | 'dark') => {
  const { surface, text } = tokens.theme[mode];

  return {
    itemHoverColor: text.heading,
    itemSelectedColor: text.heading,
    itemActiveColor: text.heading,
    cardBg: surface.main.cards
  };
};

/**
 * antd wants numbers; the theme carries px strings so emotion can interpolate them directly.
 *
 * All 17 keys are set, not just the 6 bases: antd 5.6.4 does not re-derive the *Min/*Max aliases when
 * a base is overridden, so moving screenMD to 900 while screenSMMax stays 767 leaves 768-899 uncovered.
 */
const toScreenTokens = ({ breakpoints }: Env0Theme) => {
  const px = (step: keyof Breakpoints) => parseInt(breakpoints[step], 10);
  const xs = px('xs');
  const sm = px('sm');
  const md = px('md');
  const lg = px('lg');
  const xl = px('xl');
  const xxl = px('xxl');

  return {
    screenXS: xs,
    screenXSMin: xs,
    screenXSMax: sm - 1,
    screenSM: sm,
    screenSMMin: sm,
    screenSMMax: md - 1,
    screenMD: md,
    screenMDMin: md,
    screenMDMax: lg - 1,
    screenLG: lg,
    screenLGMin: lg,
    screenLGMax: xl - 1,
    screenXL: xl,
    screenXLMin: xl,
    screenXLMax: xxl - 1,
    screenXXL: xxl,
    screenXXLMin: xxl
  };
};

export const getAntdTheme = (theme: Env0Theme): ThemeConfig => {
  if (!theme.isNewDesign) {
    return getLegacyAntdTheme(theme);
  }

  // getGeneratedAntdTheme returns a module-level object, so every addition has to be a copy.
  const generated = getGeneratedAntdTheme(theme.mode);

  return {
    ...generated,
    token: { ...generated.token, ...zIndexTokens, ...toScreenTokens(theme) },
    components: merge({}, generated.components, zIndexComponents, feedbackComponents(theme.mode), {
      Button: buttonComponent(theme.mode),
      Menu: menuComponent(theme.mode),
      Tabs: tabsComponent(theme.mode)
    })
  };
};
